import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';

import { MsalService } from '@azure/msal-angular';
import { Store, select } from '@ngrx/store';
import { combineLatest, firstValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';

import { DeviceGroupSelectors } from 'src/app/features/device-group';
import { MsalAuthSelectors } from 'src/app/features/msal-auth';
import { PackageUsersSelectors } from 'src/app/features/package-users';
import { PackagesSelectors } from 'src/app/features/packages';
import { environment } from 'src/environments/environment';

import { AvTelemetryBufferRow } from '../models/av-telemetry-buffer.model';
import {
  AV_TELEMETRY_UPLOAD_CONFIG,
  AvTelemetryUploadConfig,
  AvTelemetryUploadResult,
} from '../models/av-telemetry-upload.model';
import { AvTelemetryBufferStore } from './av-telemetry-buffer.store';

/**
 * Ships buffered telemetry to the ingest endpoint as gzipped NDJSON.
 *
 * Reads the store, not `flushed$`, which cannot see rows a failed POST left behind.
 */
@Injectable({ providedIn: 'root' })
export class AvTelemetryUploaderService implements OnDestroy {
  static readonly INGEST_ROUTE = 'avTelemetry';

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private deviceCode = '';
  private lastSevereUploadAt = 0;
  private inFlight: Promise<AvTelemetryUploadResult> | null = null;
  private listenersBound = false;

  /** Kept warm because `pagehide` cannot await `acquireTokenSilent`. */
  private teardownHeaders: Record<string, string> | null = null;

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      void this.upload();
    }
  };

  private readonly onPageHide = (): void => {
    void this.uploadOnTeardown();
  };

  constructor(
    private readonly http: HttpClient,
    private readonly bufferStore: AvTelemetryBufferStore,
    private readonly store: Store,
    private readonly msalService: MsalService,
    @Inject(AV_TELEMETRY_UPLOAD_CONFIG) private readonly config: AvTelemetryUploadConfig
  ) {}

  ngOnDestroy(): void {
    void this.stop();
  }

  /** Orphan pickup is deliberately not wired here — it belongs to 298375. */
  async start(deviceCode: string): Promise<void> {
    if (this.intervalId !== null) {
      return;
    }

    this.deviceCode = deviceCode ?? '';

    this.intervalId = setInterval(() => void this.upload(), this.config.uploadIntervalMs);
    this.bindTeardownListeners();

    // Covers a session that ends before the first cadence tick.
    await this.refreshTeardownHeaders();
  }

  async stop(): Promise<void> {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.unbindTeardownListeners();

    await this.upload();
  }

  async uploadForEvent(eventName: string): Promise<AvTelemetryUploadResult> {
    if (!this.config.severeEventNames.includes(eventName)) {
      return { outcome: 'skipped', rowsUploaded: 0 };
    }

    const now = Date.now();
    if (now - this.lastSevereUploadAt < this.config.severeDebounceMs) {
      return { outcome: 'skipped', rowsUploaded: 0 };
    }

    const result = await this.upload();

    // A skipped run never reached the network, so it must not suppress a genuine severe event
    // moments later. Everything else consumes the window — including a failure, because severe
    // events cluster during the same degradation that makes POSTs fail, and exempting them would
    // turn one outage into a POST per event. The 60s cadence still re-ships buffered rows.
    if (result.outcome !== 'skipped') {
      this.lastSevereUploadAt = now;
    }

    return result;
  }

  /** Serialised: concurrent triggers would otherwise read the same rows and POST duplicates. */
  async upload(): Promise<AvTelemetryUploadResult> {
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.runUpload().finally(() => (this.inFlight = null));

    return this.inFlight;
  }

  private async runUpload(): Promise<AvTelemetryUploadResult> {
    // BaseUrlInterceptor interpolates the store value, so an unset package sends "undefined"
    // verbatim and ingest 400s.
    if (!(await this.isIdentityHydrated())) {
      return { outcome: 'skipped', rowsUploaded: 0 };
    }

    const rows = await this.readShippableRows();
    if (!rows.length) {
      return { outcome: 'nothingToUpload', rowsUploaded: 0 };
    }

    let uploaded = 0;

    for (const batch of this.batch(rows)) {
      const body = await this.gzip(this.toNdjson(batch));
      if (!body) {
        return { outcome: 'failed', rowsUploaded: uploaded };
      }

      const sent = await this.post(body);
      if (!sent) {
        return { outcome: 'failed', rowsUploaded: uploaded };
      }

      // Deleting before the response confirms would discard telemetry that never landed.
      await this.bufferStore.deleteByIds(this.idsOf(batch));
      uploaded += batch.length;
    }

    // Not awaited: this only maintains the teardown cache, and callers of upload() should not
    // block on an MSAL round trip that has no bearing on the result.
    void this.refreshTeardownHeaders();

    return { outcome: 'uploaded', rowsUploaded: uploaded };
  }

  /**
   * Snapshots what BaseUrlInterceptor would otherwise add. A failure keeps the previous snapshot:
   * clearing it would disable teardown flushing after one transient MSAL hiccup.
   */
  private async refreshTeardownHeaders(): Promise<void> {
    try {
      const [packageGuid, packageUserGuid, packageId, deviceCode] = await firstValueFrom(
        combineLatest([
          this.store.pipe(select(PackagesSelectors.getActivePackageGuid)),
          this.store.pipe(select(PackageUsersSelectors.getActivePackageUserGuid)),
          this.store.pipe(select(PackagesSelectors.getActivePackageId)),
          this.store.pipe(select(DeviceGroupSelectors.getDeviceCode)),
        ]).pipe(first())
      );

      const token = await this.acquireAccessToken();
      if (!token || !packageGuid || !packageUserGuid || !packageId) {
        return;
      }

      this.teardownHeaders = {
        Authorization: `Bearer ${token}`,
        'auth-settings': 'user-bearer',
        'package-guid': `${packageGuid}`,
        'package-user-guid': `${packageUserGuid}`,
        'package-id': `${packageId}`,
        'device-code': `${deviceCode ?? this.deviceCode}`,
      };
    } catch {
      // Telemetry must never disrupt the session it observes.
    }
  }

  /**
   * Uses MSAL's default authority, unlike MSALInterceptorConfigFactory which reads the account's
   * `acr`/`tfp` claim. May differ for secure-link and signing-agent logins.
   */
  private async acquireAccessToken(): Promise<string | null> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      return null;
    }

    const result = await firstValueFrom(
      this.msalService.acquireTokenSilent({
        account: account,
        scopes: environment.consentScopes,
      })
    );

    return result?.accessToken ?? null;
  }

  private async uploadOnTeardown(): Promise<void> {
    // An in-flight cadence upload already owns these rows. Shipping them again here would
    // double-POST them, since it cannot observe that POST's outcome before the page dies.
    if (this.inFlight) {
      return;
    }

    if (!(await this.isIdentityHydrated())) {
      return;
    }

    const rows = await this.readShippableRows();
    if (!rows.length) {
      return;
    }

    const body = await this.gzip(this.toNdjson(rows));
    if (!body || body.size > this.config.maxKeepaliveBytes) {
      return;
    }

    const sent = await this.postKeepalive(body);
    if (sent) {
      await this.bufferStore.deleteByIds(this.idsOf(rows));
    }
  }

  /**
   * Reads the current value rather than waiting: a parked upload would outlive its trigger.
   *
   * Mirrors BaseUrlInterceptor's own fallback to the id-token claim, so this gate never blocks an
   * upload the interceptor would have sent a valid header for.
   */
  private async isIdentityHydrated(): Promise<boolean> {
    const [packageGuid, claimPackageGuid] = await firstValueFrom(
      combineLatest([
        this.store.pipe(select(PackagesSelectors.getActivePackageGuid)),
        this.store.pipe(select(MsalAuthSelectors.getPackageGuid)),
      ]).pipe(first())
    );

    return !!(packageGuid ?? claimPackageGuid);
  }

  private async readShippableRows(): Promise<AvTelemetryBufferRow[]> {
    const rows = await this.bufferStore.readAll();

    // Without an id there is nothing to delete after a successful POST.
    return rows.filter((row) => typeof row.id === 'number');
  }

  private idsOf(rows: readonly AvTelemetryBufferRow[]): number[] {
    return rows.map((row) => row.id).filter((id): id is number => typeof id === 'number');
  }

  private batch(rows: readonly AvTelemetryBufferRow[]): AvTelemetryBufferRow[][] {
    const batches: AvTelemetryBufferRow[][] = [];

    for (let index = 0; index < rows.length; index += this.config.maxRowsPerBatch) {
      batches.push(rows.slice(index, index + this.config.maxRowsPerBatch));
    }

    return batches;
  }

  private toNdjson(rows: readonly AvTelemetryBufferRow[]): string {
    return rows.map((row) => JSON.stringify(row.envelope)).join('\n') + '\n';
  }

  private async post(body: Blob): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.post(AvTelemetryUploaderService.INGEST_ROUTE, body, {
          headers: { 'Content-Encoding': 'gzip', 'Content-Type': 'application/x-ndjson' },
        })
      );

      return true;
    } catch {
      return false;
    }
  }

  // Seams: CompressionStream and fetch are host globals, so the specs override these.

  protected async gzip(payload: string): Promise<Blob | null> {
    try {
      const stream = new Blob([payload]).stream().pipeThrough(new CompressionStream('gzip'));

      return await new Response(stream).blob();
    } catch {
      return null;
    }
  }

  /** keepalive survives the page but bypasses the interceptors, so headers are set by hand. */
  protected async postKeepalive(body: Blob): Promise<boolean> {
    const headers = this.teardownHeaders;
    if (!headers) {
      return false;
    }

    try {
      const response = await fetch(
        `${environment.apiBaseUrl}${AvTelemetryUploaderService.INGEST_ROUTE}`,
        {
          method: 'POST',
          keepalive: true,
          headers: {
            ...headers,
            'Content-Encoding': 'gzip',
            'Content-Type': 'application/x-ndjson',
          },
          body: body,
        }
      );

      return response.ok;
    } catch {
      return false;
    }
  }

  private bindTeardownListeners(): void {
    if (this.listenersBound) {
      return;
    }

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('pagehide', this.onPageHide);
    this.listenersBound = true;
  }

  private unbindTeardownListeners(): void {
    if (!this.listenersBound) {
      return;
    }

    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('pagehide', this.onPageHide);
    this.listenersBound = false;
  }
}
